import { randomBytes } from "node:crypto";

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Shares a job with another person by email, read-only, without transferring
 * ownership. Always inserts as `pending` — even when that email already has
 * an account we can find (via `profiles.email` — populated once someone
 * uploads a file or visits their account page, not on every login, so this
 * is a best-effort shortcut, not the source of truth) — because
 * jobs_select_shared/results_select_shared only grant real content access
 * once status='active', and the recipient must explicitly accept
 * (`respondToShare`) before that happens. This function alone never grants
 * anything by itself: an existing-account match still needs an accept, and
 * a token invite still needs `claimInvite`. (Previously an existing-account
 * match activated immediately with no recipient action — a real spam vector,
 * fixed 2026-09-05.)
 */
export const shareJob = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        jobId: z.string().uuid(),
        email: z.string().trim().email().max(255),
        canDownload: z.boolean(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId, claims } = context;
    const email = data.email.toLowerCase();
    const ownerEmail = (claims as { email?: string } | null)?.email ?? null;

    const { data: job, error: jobError } = await supabase
      .from("jobs")
      .select("id, user_id")
      .eq("id", data.jobId)
      .eq("user_id", userId)
      .maybeSingle();
    if (jobError) throw new Error(jobError.message);
    if (!job) throw new Error("You can only share a file you uploaded yourself.");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: existing } = await supabaseAdmin
      .from("profiles")
      .select("user_id, email")
      .ilike("email", email)
      .maybeSingle();

    if (existing?.user_id === userId) {
      throw new Error("You can't share a file with yourself.");
    }

    if (existing?.user_id) {
      const { error } = await supabaseAdmin.from("job_shares").insert({
        job_id: data.jobId,
        owner_id: userId,
        shared_with_user_id: existing.user_id,
        invited_email: email,
        can_download: data.canDownload,
        status: "pending",
      });
      if (error) throw new Error(shareInsertErrorMessage(error));

      try {
        await sendShareInviteEmail({ recipientEmail: email, ownerEmail, token: null });
      } catch (mailError) {
        console.error("[sharing] failed to send share notification:", mailError);
      }
      return { ok: true, status: "pending" as const };
    }

    const token = randomBytes(24).toString("hex");
    const { error } = await supabaseAdmin.from("job_shares").insert({
      job_id: data.jobId,
      owner_id: userId,
      invited_email: email,
      can_download: data.canDownload,
      status: "pending",
      invite_token: token,
    });
    if (error) throw new Error(shareInsertErrorMessage(error));

    try {
      await sendShareInviteEmail({ recipientEmail: email, ownerEmail, token });
    } catch (mailError) {
      console.error("[sharing] failed to send invite email:", mailError);
    }
    return { ok: true, status: "pending" as const };
  });

function shareInsertErrorMessage(error: { code?: string; message: string }): string {
  // Postgres unique_violation on job_shares_active_unique
  if (error.code === "23505") return "This file is already shared with that person.";
  return error.message;
}

function siteUrl(): string {
  return process.env["SITE_URL"] ?? "https://understandyourgenome.org";
}

/**
 * Shared by both the initial send (shareJob) and resendShareInvite — same
 * two variants either way (matched existing account vs. anonymous token
 * invite), always CC'd to the owner so they have a record the email
 * actually went out, not just that a job_shares row exists.
 */
async function sendShareInviteEmail(params: {
  recipientEmail: string;
  ownerEmail: string | null;
  token: string | null;
}): Promise<void> {
  const { sendMail } = await import("@/lib/mail.server");
  const cc = params.ownerEmail ? [params.ownerEmail] : undefined;
  if (params.token) {
    await sendMail(
      [params.recipientEmail],
      "You've been invited to view a genome report",
      `Someone invited you to view an annotated genome report on Understand Your Genome.\n\n` +
        `Sign in or create a free account, then open this link to view it:\n${siteUrl()}/invite/${params.token}`,
      { cc },
    );
  } else {
    await sendMail(
      [params.recipientEmail],
      "Someone wants to share a genome report with you",
      `Someone wants to share an annotated genome report with you on Understand Your Genome.\n\n` +
        `Sign in and visit your dashboard to accept or decline: ${siteUrl()}/dashboard`,
      { cc },
    );
  }
}

export const listJobShares = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ jobId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: job } = await supabase
      .from("jobs")
      .select("id")
      .eq("id", data.jobId)
      .eq("user_id", userId)
      .maybeSingle();
    if (!job) throw new Error("You can only manage sharing for a file you uploaded yourself.");

    const { data: shares, error } = await supabase
      .from("job_shares")
      .select("id, invited_email, can_download, status, created_at")
      .eq("job_id", data.jobId)
      .neq("status", "revoked")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return shares;
  });

export const revokeJobShare = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ shareId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("job_shares")
      .update({ status: "revoked", revoked_at: new Date().toISOString() })
      .eq("id", data.shareId)
      .eq("owner_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const setShareDownload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ shareId: z.string().uuid(), canDownload: z.boolean() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("job_shares")
      .update({ can_download: data.canDownload })
      .eq("id", data.shareId)
      .eq("owner_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/**
 * Owner-only: re-sends the exact same invite/notification email as the
 * original shareJob call — for when it never arrived (spam filter, typo
 * caught after the fact via a revoke+re-share, etc.). Only while still
 * `pending`; an active/declined/revoked share has nothing meaningful left
 * to resend. Reuses sendShareInviteEmail so the matched-account vs.
 * token-invite variant (and the owner CC) stay identical to the original.
 */
export const resendShareInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ shareId: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId, claims } = context;
    const ownerEmail = (claims as { email?: string } | null)?.email ?? null;

    const { data: share, error } = await supabase
      .from("job_shares")
      .select("invited_email, invite_token, status")
      .eq("id", data.shareId)
      .eq("owner_id", userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!share) throw new Error("Share not found.");
    if (share.status !== "pending") throw new Error("This invite is no longer pending.");
    if (!share.invited_email) throw new Error("No email on file for this share.");

    await sendShareInviteEmail({
      recipientEmail: share.invited_email,
      ownerEmail,
      token: share.invite_token,
    });
    return { ok: true };
  });

/**
 * Attaches a pending share to whichever account is calling this — the token
 * is the security boundary, not an exact match against the originally
 * invited email (someone might sign up with Google under a different
 * address than the one invited; that's fine, the link itself was only ever
 * sent to the person meant to have it). Runs via the service-role client
 * since the recipient isn't the owner and doesn't yet match
 * shared_with_user_id, so no ordinary RLS policy can express this write.
 */
export const claimInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ token: z.string().min(1).max(200) }).parse(input))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: share, error } = await supabaseAdmin
      .from("job_shares")
      .select("id, job_id, status, shared_with_user_id")
      .eq("invite_token", data.token)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!share) throw new Error("This invite link isn't valid.");
    if (share.status === "revoked") throw new Error("This invite has been revoked.");
    if (share.status === "active") {
      // Already claimed. Only the person who claimed it re-clicking their
      // own email link is fine (idempotent) — anyone else hitting the same
      // token must not silently inherit someone else's access grant.
      if (share.shared_with_user_id === userId) return { jobId: share.job_id };
      throw new Error("This invite has already been used by someone else.");
    }

    // `.eq("status", "pending")` here isn't redundant with the check above —
    // it closes the race where two callers both pass that check (both see
    // "pending") before either writes. Only the update that actually lands
    // on a still-pending row succeeds; the loser gets 0 rows back and must
    // not treat that as success, or two people could both believe they'd
    // claimed the same invite while only one of them actually has.
    const { data: updated, error: updateError } = await supabaseAdmin
      .from("job_shares")
      .update({
        shared_with_user_id: userId,
        status: "active",
        claimed_at: new Date().toISOString(),
      })
      .eq("id", share.id)
      .eq("status", "pending")
      .select("job_id")
      .maybeSingle();
    if (updateError) throw new Error(updateError.message);
    if (!updated) {
      // Someone else's claim (or a revoke) landed first between our SELECT
      // and UPDATE. Re-check what actually happened so the error is honest.
      const { data: latest } = await supabaseAdmin
        .from("job_shares")
        .select("status, shared_with_user_id")
        .eq("id", share.id)
        .maybeSingle();
      if (latest?.status === "active" && latest.shared_with_user_id === userId) {
        return { jobId: share.job_id };
      }
      throw new Error("This invite has already been used by someone else.");
    }
    return { jobId: updated.job_id };
  });

/**
 * Incoming shares awaiting this user's accept/decline — only the
 * matched-existing-account path (see shareJob) ever lands here with
 * shared_with_user_id already set while still 'pending'; a token invite
 * stays anonymous (shared_with_user_id null) until claimInvite runs, so it
 * never shows up in this list. jobs/profiles are looked up via the
 * service-role client since RLS won't show either table for a job that
 * isn't actively shared yet — only enough fields to label the invite are
 * selected, no annotation content.
 */
export const listPendingShares = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: shares, error } = await supabase
      .from("job_shares")
      .select("id, job_id, owner_id, can_download, created_at")
      .eq("shared_with_user_id", userId)
      .eq("status", "pending")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    if (!shares || shares.length === 0) return [];

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const jobIds = Array.from(new Set(shares.map((s) => s.job_id)));
    const ownerIds = Array.from(new Set(shares.map((s) => s.owner_id)));
    const [{ data: jobs }, { data: owners }] = await Promise.all([
      supabaseAdmin.from("jobs").select("id, filename, label").in("id", jobIds),
      supabaseAdmin.from("profiles").select("user_id, email").in("user_id", ownerIds),
    ]);
    const jobById = new Map((jobs ?? []).map((j) => [j.id, j]));
    const emailByOwnerId = new Map((owners ?? []).map((o) => [o.user_id, o.email]));

    return shares.map((s) => {
      const job = jobById.get(s.job_id);
      return {
        shareId: s.id as string,
        jobId: s.job_id as string,
        filename: (job?.label || job?.filename) ?? "a file",
        ownerEmail: emailByOwnerId.get(s.owner_id) ?? "someone",
        canDownload: s.can_download as boolean,
        createdAt: s.created_at as string,
      };
    });
  });

/**
 * Recipient accepts or declines their own pending share. Runs through the
 * regular RLS-scoped client (not admin) — job_shares_update_recipient
 * (migration add_share_accept_decline.sql) is exactly narrow enough to
 * allow this: only the matched recipient, only while still 'pending', only
 * into 'active'/'declined'. The `.eq("status", "pending")` guard mirrors
 * claimInvite's race-safety idiom (only the write that actually lands on a
 * still-pending row succeeds).
 */
export const respondToShare = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ shareId: z.string().uuid(), decision: z.enum(["accept", "decline"]) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const now = new Date().toISOString();
    const status = data.decision === "accept" ? "active" : "declined";
    const update =
      data.decision === "accept" ? { status, claimed_at: now } : { status, declined_at: now };
    const { data: updated, error } = await supabase
      .from("job_shares")
      .update(update)
      .eq("id", data.shareId)
      .eq("shared_with_user_id", userId)
      .eq("status", "pending")
      .select("id")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!updated) throw new Error("This invite is no longer pending.");
    return { ok: true, status };
  });
